/* Share > Profile PDF: Profilblätter erzeugen (Stufe 4).
 *
 * Erzeugt aus den markierten Spielern je ein einseitiges Profilblatt (export/playerProfilePdf.js) — eine Datei mit
 * einer Seite je Spieler oder getrennte Dateien — und übergibt sie dem Teilen-Ablauf (useShareFiles.runWith).
 * Nichts wird gespeichert, nichts hochgeladen; die Datei entsteht im Browser.
 *
 * Datenschutz: Inhalt ausschließlich über domain/profileSheet.js (feste Feldliste), für jede Rolle gleich.
 *
 * Bilder: Foto in der mittleren Fassung (nicht das Original), Logo als kleine Fassung; per Canvas in JPEG/PNG
 * gewandelt (export/imageForPdf.js). Fehlt ein Bild oder schlägt das Laden fehl, erscheint ein Platzhalter —
 * das PDF wird trotzdem erzeugt.
 */

import { signedImageUrl } from '../supabase/storage.js';
import { useFootballClubs } from './useFootballClubs.js';
import { loadTransfersFor, transfersOf } from './useTransfers.js';
import { XFER_MAX_ROWS } from '../export/cardGeometry.js';
import { buildProfile } from '../domain/profileSheet.js';
import { mapPool, playerFilename, LOAD_CONCURRENCY, LOAD_TIMEOUT_MS, createCancel } from '../domain/shareFlow.js';
import { localIsoDate } from '../domain/playerListExport.js';
import { PROFILE_LABELS, profileFooter, profileDate } from '../export/exportLabels.js';
import { loadProfileFonts } from '../export/pdfFonts.js';
import { downloadFile } from '../export/download.js';
import { photoToJpeg, logoToPng } from '../export/imageForPdf.js';

/** Bild über einen signierten Link laden (privater Bucket); wirft bei Fehler. */
async function fetchImage(path, variant) {
  const href = await signedImageUrl(path, variant);
  if (!href) throw new Error('noUrl');
  const res = await fetch(href);
  if (!res.ok) throw new Error('loadFailed');
  return res.blob();
}

/** Foto und Logo eines Profils; jedes Bild unabhängig — ein Fehler ergibt nur "null" (Platzhalter). */
/** Wappen von Grassrunners FC aus public/ (für Spieler ohne Vereinseintrag, aber mit "seit"); wird auf höchstens 128 px verkleinert. */
async function fetchCrest() {
  const res = await fetch('/brand-mark.png');
  if (!res.ok) throw new Error('loadFailed');
  return res.blob();
}

/* Pfade, deren Ladefehler schon in der Konsole stehen: je Logo nur einmal melden, nicht bei jeder Karte neu */
const reportedLogos = new Set();

/**
 * Ein Vereinslogo als PNG-Daten für die Karte — die eine Stelle für das Feld "Club" UND die Transferhistorie.
 * Jeder Fehler (gesperrt, nicht gefunden, Format nicht lesbar) ergibt null: dann steht nur der Vereinsname. Der Fehler
 * kommt einmal je Logo in die Konsole, nie in die Oberfläche.
 * @param {string} key  Pfad (nur für die Meldung)
 * @param {() => Promise<Blob>} getBlob
 */
export async function loadLogoImage(key, getBlob, toPng) {
  try {
    return await toPng(await getBlob());
  } catch (e) {
    if (!reportedLogos.has(key)) {
      reportedLogos.add(key);
      console.error('[profilePdf] Logo nicht verfügbar:', key, e && e.message);
    }
    return null;
  }
}

/** Logos der Transferhistorie (nur der gezeigten Zeilen, je Pfad einmal); ein Fehler ergibt nur "kein Logo" (der Name bleibt). */
async function transferLogosFor(profile, { fetchBlob, toPng }) {
  const paths = new Set();
  for (const t of (profile.transfers || []).slice(0, XFER_MAX_ROWS)) {
    if (t.from.logoPath) paths.add(t.from.logoPath);
    if (t.to.logoPath) paths.add(t.to.logoPath);
  }
  const out = {};
  await Promise.all([...paths].map(async (path) => {
    out[path] = await loadLogoImage(path, () => fetchBlob(path, 'md'), toPng);
  }));
  return out;
}

export async function prepareImages(profile, { fetchBlob = fetchImage, fetchOwnCrest = fetchCrest, toJpeg = photoToJpeg, toPng = logoToPng } = {}) {
  const [photo, logo, transferLogos] = await Promise.all([
    profile.photoPath
      ? (async () => {
          /* Content-Type der Antwort und Endung des Pfades nur für die Konsole (Fehlersuche auf Geräten ohne Zugriff) */
          let type = '-';
          try {
            const b = await fetchBlob(profile.photoPath, 'md');
            type = (b && b.type) || '(leer)';
            return await toJpeg(b);
          } catch (e) {
            const ext = (String(profile.photoPath).match(/\.([a-z0-9]+)$/i) || [])[1] || '-';
            console.error('[profilePdf] Foto nicht verfügbar:', e && e.message, `(Content-Type: ${type}, Endung: ${ext})`);
            return null;
          }
        })()
      : null,
    profile.clubFallback || profile.clubLogoPath
      ? loadLogoImage(
          profile.clubFallback ? 'own-crest' : profile.clubLogoPath,
          () => (profile.clubFallback ? fetchOwnCrest() : fetchBlob(profile.clubLogoPath, 'md')),
          toPng
        )
      : null,
    transferLogosFor(profile, { fetchBlob, toPng }),
  ]);
  return { photo, logo, transferLogos };
}

/** Ein PNG aus public/ als Data-URL. Das AFH-Logo kommt in 128 px (ca. 11 statt 82 KB für das 720-px-Original); fehlt es, wird ohne gezeichnet. */
async function loadPublicPng(path) {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const bytes = new Uint8Array(await res.arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return `data:image/png;base64,${btoa(bin)}`;
  } catch {
    return null;
  }
}

/**
 * Erzeuger für useShareFiles.runWith.
 *
 * @param {Array} players  markierte Spieler in Listenreihenfolge
 * @param {{ mode: 'one'|'separate', now?: Date }} opts
 * @returns {(ctx: { cancel: object, onProgress: Function }) => Promise<{ files, failed, cancelled }>}
 */
export function profileProducer(players, { mode = 'one', now = new Date(), clubsById = null, deps = {} } = {}) {
  return async ({ cancel = createCancel(), onProgress = () => {} } = {}) => {
    /* Transferhistorie nur für transferierte Spieler, aus dem gemeinsamen Speicher (dieselbe Liste wie der Reiter Transfer Info).
       Zeitgrenze je Spieler; Fehler oder Zeitüberschreitung ergeben "keine Historie", nie einen Abbruch. */
    const load = deps.loadTransfers || ((p) => loadTransfersFor(p.id));
    const transferPool = await mapPool(
      players,
      (p) => (p.status === 'transferred' ? load(p) : Promise.resolve([])),
      { concurrency: LOAD_CONCURRENCY, timeoutMs: LOAD_TIMEOUT_MS, cancel }
    );
    if (transferPool.cancelled) return { files: [], failed: [], cancelled: true };
    const profiles = players.map((p, i) => buildProfile(p, { clubsById, now, transfers: transferPool.results[i] || null }));

    /* Bilder: höchstens 3 Spieler gleichzeitig, je Spieler 30 s; Fortschritt = fertige Spieler */
    const pool = await mapPool(
      profiles,
      (profile) => prepareImages(profile, deps),
      { concurrency: LOAD_CONCURRENCY, timeoutMs: LOAD_TIMEOUT_MS, cancel, onProgress }
    );
    if (pool.cancelled) return { files: [], failed: [], cancelled: true };

    /* Ein Spieler, dessen Bilder zu lange brauchten, bekommt trotzdem sein Blatt — mit Platzhaltern */
    const entries = profiles.map((profile, i) => ({
      profile,
      photo: pool.results[i] ? pool.results[i].photo : null,
      logo: pool.results[i] ? pool.results[i].logo : null,
      transferLogos: pool.results[i] ? pool.results[i].transferLogos : {},
    }));

    /* jsPDF erst jetzt laden: eigener Chunk, belastet den Start der App nicht */
    /* Schriften (Zeitlimit 8 s, sonst Helvetica) und Logo gleichzeitig; keines davon darf den Export kippen */
    const [{ renderProfilePdf }, afh, fonts] = await Promise.all([
      deps.loadRenderer ? deps.loadRenderer() : import('../export/playerProfilePdf.js'),
      deps.loadAfh ? deps.loadAfh() : loadPublicPng('/afh-white-360.png'),
      Promise.resolve().then(() => (deps.loadFonts ? deps.loadFonts() : loadProfileFonts())).catch(() => null),
    ]);
    if (cancel.cancelled) return { files: [], failed: [], cancelled: true };

    const footer = profileFooter();
    const used = new Set();
    /* Ein Spieler oder "Separate files": je Spieler eine Datei; sonst ein PDF, eine Seite je Spieler */
    const separate = mode === 'separate' || entries.length === 1;
    const groups = separate ? entries.map((e) => [e]) : [entries];
    const files = groups.map((group, gi) => {
      const doc = renderProfilePdf(group, { labels: PROFILE_LABELS, footer, date: profileDate(now), afh, fonts });
      const filename = separate
        ? playerFilename(players[gi], 'PlayerCard', used)
        : `AFH_PlayerCards_${group.length}_${localIsoDate(now)}.pdf`;
      return { filename, blob: doc.output('blob') };
    });
    return { files, failed: [], cancelled: false };
  };
}

export function useProfilePdf() {
  const clubRegister = useFootballClubs();
  /** @param {Array} players @param {{ mode?: 'one'|'separate' }} [opts] */
  function producerFor(players, opts = {}) {
    return profileProducer(players, { ...opts, clubsById: clubRegister.byId.value });
  }
  /**
   * Die Karte EINES Spielers erzeugen und herunterladen (Knopf im Reiter Material). Fehlendes (Foto, Highlight,
   * Matches, Schlagwörter) führt nie zu einem Fehler. Liefert den Dateinamen oder null.
   */
  async function cardFileFor(player) {
    const out = await profileProducer([player], { clubsById: clubRegister.byId.value })({});
    return (out.files && out.files[0]) || null;
  }
  async function downloadCard(player) {
    const file = await cardFileFor(player);
    if (!file) return null;
    downloadFile(file.blob, file.filename);
    return file.filename;
  }
  /** Das PDF der Karte als Blob — dieselbe Funktion wie der Download (Vorschau im Reiter Material). */
  async function cardBlobFor(player) {
    const file = await cardFileFor(player);
    if (!file) throw new Error('noCard');
    return file.blob;
  }
  /** Die schon geladene Transferhistorie eines Spielers für Layout und Datenstand der Vorschau (leer, solange nicht geladen). */
  const previewTransfers = (player) => (player && player.status === 'transferred' ? transfersOf(player.id) : null);
  return { producerFor, downloadCard, cardBlobFor, previewTransfers };
}
